'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Crown, Check, X } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { ALL_PREMIUM_FEATURES, PREMIUM_FEATURE_LABELS } from '@/lib/subscription'
import type { PremiumFeature } from '@/lib/types'

interface UpgradePromptProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The feature the user tried to access — will be highlighted. */
  feature?: PremiumFeature
}

export function UpgradePrompt({ open, onOpenChange, feature }: UpgradePromptProps) {
  const router = useRouter()

  const handleUpgrade = () => {
    onOpenChange(false)
    router.push('/pricing')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col">
        <DialogTitle className="sr-only">Upgrade to Penthouse</DialogTitle>

        {/* Hero */}
        <div className="bg-gradient-to-br from-amber-500/10 via-primary/5 to-primary/10 px-6 pt-6 pb-5 border-b border-border/50 text-center shrink-0">
          <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg">
            <Crown className="h-7 w-7 text-white" />
          </div>
          <h2 className="text-xl font-bold text-foreground tracking-tight">
            Upgrade to Penthouse
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Unlock the full power of Roostr
          </p>
        </div>

        {/* Feature list */}
        <div className="px-6 py-5 space-y-3 overflow-y-auto flex-1 min-h-0">
          {feature && (
            <p className="text-sm text-muted-foreground mb-4">
              <span className="font-medium text-foreground">{PREMIUM_FEATURE_LABELS[feature].name}</span>{' '}
              is a Penthouse feature.{' '}
              {PREMIUM_FEATURE_LABELS[feature].description}
            </p>
          )}

          <div className="space-y-2.5">
            {ALL_PREMIUM_FEATURES.map((f) => {
              const info = PREMIUM_FEATURE_LABELS[f]
              const isHighlighted = f === feature
              return (
                <div
                  key={f}
                  className={cn(
                    'flex items-start gap-3 rounded-lg px-3 py-2 transition-colors',
                    isHighlighted
                      ? 'bg-primary/5 ring-1 ring-primary/20'
                      : ''
                  )}
                >
                  <Check className={cn(
                    'h-4 w-4 mt-0.5 flex-shrink-0',
                    isHighlighted ? 'text-primary' : 'text-emerald-500'
                  )} />
                  <div>
                    <p className="text-sm font-medium text-foreground">{info.name}</p>
                    <p className="text-xs text-muted-foreground">{info.description}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* CTA */}
        <div className="px-6 pb-6 space-y-3 shrink-0">
          <Button
            className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white shadow-md"
            size="lg"
            onClick={handleUpgrade}
          >
            <Crown className="h-4 w-4 mr-2" />
            View Pricing
          </Button>
          <button
            onClick={() => onOpenChange(false)}
            className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            Maybe later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
